import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Save, Trash2, X, Power, PowerOff } from 'lucide-react';

/**
 * Универсальный CRUD-менеджер справочников units / currencies для супер-админа.
 * kind: 'unit' | 'currency'
 */
function ReferenceManager({ kind }) {
  const isUnit = kind === 'unit';
  const cfg = isUnit
    ? {
        title: 'Единицы измерения',
        endpoint: '/units',
        cols: [
          { key: 'code', label: 'Код', placeholder: 'т, шт, кг…', width: 'w-24' },
          { key: 'label_ru', label: 'Название (RU)', placeholder: 'тонна', width: 'flex-1' },
          { key: 'label_en', label: 'Название (EN)', placeholder: 'ton', width: 'flex-1' },
        ],
        emptyRow: { code: '', label_ru: '', label_en: '' },
        testidPrefix: 'unit',
      }
    : {
        title: 'Валюты',
        endpoint: '/currencies',
        cols: [
          { key: 'code', label: 'Код (ISO)', placeholder: 'USD', width: 'w-28' },
          { key: 'label', label: 'Название', placeholder: 'Доллар США', width: 'flex-1' },
          { key: 'symbol', label: 'Символ', placeholder: '$', width: 'w-20' },
        ],
        emptyRow: { code: '', label: '', symbol: '' },
        testidPrefix: 'currency',
      };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(cfg.emptyRow);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${cfg.endpoint}?include_inactive=1`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err, 'Не удалось загрузить'));
    } finally {
      setLoading(false);
    }
  }, [cfg.endpoint]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!(draft.code || '').trim()) {
      toast.error('Укажите код');
      return;
    }
    try {
      await api.post(cfg.endpoint, draft);
      toast.success('Создано');
      setDraft(cfg.emptyRow);
      load();
    } catch (err) {
      toast.error(formatApiError(err, 'Не удалось создать'));
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditDraft({ ...it });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({});
  };

  const saveEdit = async () => {
    try {
      await api.put(`${cfg.endpoint}/${editingId}`, editDraft);
      toast.success('Обновлено');
      cancelEdit();
      load();
    } catch (err) {
      toast.error(formatApiError(err, 'Не удалось обновить'));
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`${cfg.endpoint}/${it.id}`, { is_active: !it.is_active });
      toast.success(it.is_active ? 'Деактивировано' : 'Активировано');
      load();
    } catch (err) {
      toast.error(formatApiError(err, 'Не удалось изменить'));
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Удалить «${it.code}»? Если используется в тендерах — будет деактивировано (скрыто из форм).`)) return;
    try {
      const { data } = await api.delete(`${cfg.endpoint}/${it.id}`);
      toast.success(data?.message || 'Удалено');
      load();
    } catch (err) {
      toast.error(formatApiError(err, 'Не удалось удалить'));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid={`${cfg.testidPrefix}s-manager`}>
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">{cfg.title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Соответствует справочнику в формах создания лота и в калькуляторе.</p>
        </div>
        <Badge variant="outline" className="rounded-sm text-xs">{items.length}</Badge>
      </div>

      {/* Create row */}
      <div className="px-5 py-3 bg-slate-50/60 border-b border-slate-200">
        <div className="flex items-end gap-2">
          {cfg.cols.map(c => (
            <div key={c.key} className={c.width}>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{c.label}</label>
              <Input
                value={draft[c.key] || ''}
                onChange={(e) => setDraft(p => ({ ...p, [c.key]: e.target.value }))}
                placeholder={c.placeholder}
                data-testid={`${cfg.testidPrefix}-new-${c.key}`}
                className="h-9 text-sm border-slate-300 rounded-sm bg-white mt-1"
              />
            </div>
          ))}
          <Button
            size="sm"
            onClick={handleCreate}
            data-testid={`${cfg.testidPrefix}-create-btn`}
            className="h-9 bg-[#0046AD] hover:bg-[#003A8C] text-white rounded-sm"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Добавить
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="divide-y divide-slate-100">
        {loading && items.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-slate-400">Загрузка…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-slate-400">Записей нет</div>
        )}
        {items.map(it => {
          const isEditing = editingId === it.id;
          return (
            <div
              key={it.id}
              className={`px-5 py-2.5 flex items-center gap-2 hover:bg-slate-50/60 ${!it.is_active ? 'bg-slate-50/40 opacity-60' : ''}`}
              data-testid={`${cfg.testidPrefix}-row-${it.id}`}
            >
              {cfg.cols.map(c => (
                <div key={c.key} className={c.width}>
                  {isEditing ? (
                    <Input
                      value={editDraft[c.key] ?? ''}
                      onChange={(e) => setEditDraft(p => ({ ...p, [c.key]: e.target.value }))}
                      data-testid={`${cfg.testidPrefix}-edit-${c.key}-${it.id}`}
                      className="h-8 text-sm border-slate-300 rounded-sm"
                    />
                  ) : (
                    <span className={`text-sm ${c.key === 'code' ? 'font-mono font-semibold text-slate-800' : 'text-slate-700'}`}>
                      {it[c.key] || <span className="text-slate-300">—</span>}
                    </span>
                  )}
                </div>
              ))}
              {!it.is_active && (
                <Badge variant="outline" className="rounded-sm text-[10px] text-amber-600 border-amber-300 bg-amber-50">
                  Деактивирована
                </Badge>
              )}
              <div className="flex items-center gap-1 ml-auto">
                {isEditing ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={saveEdit} data-testid={`${cfg.testidPrefix}-save-${it.id}`}
                      className="h-7 px-2 text-emerald-600 hover:bg-emerald-50">
                      <Save className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancelEdit}
                      className="h-7 px-2 text-slate-400 hover:bg-slate-100">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => startEdit(it)} data-testid={`${cfg.testidPrefix}-edit-${it.id}`}
                      className="h-7 px-2 text-slate-500 hover:bg-slate-100 text-xs">
                      Изменить
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(it)} data-testid={`${cfg.testidPrefix}-toggle-${it.id}`}
                      className={`h-7 px-2 ${it.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
                      title={it.is_active ? 'Деактивировать' : 'Активировать'}>
                      {it.is_active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(it)} data-testid={`${cfg.testidPrefix}-delete-${it.id}`}
                      className="h-7 px-2 text-rose-500 hover:bg-rose-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function UnitsCurrenciesManager() {
  return (
    <div className="space-y-6">
      <ReferenceManager kind="unit" />
      <ReferenceManager kind="currency" />
    </div>
  );
}
